import React, { useState, useEffect, useRef } from "react";
import { useMediaPlayer } from "@vidstack/react";
import { FaSync, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const AudioSync = ({ audioUrl, episodeId }) => {
  const player = useMediaPlayer();
  const audioRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const savedOffset = localStorage.getItem(`audio_sync_${episodeId}`);
    if (savedOffset) {
      setOffset(parseFloat(savedOffset));
    }
  }, [episodeId]);

  useEffect(() => {
    if (!player || !audioRef.current || !audioUrl) return;

    const video = player.querySelector("video");
    if (!video) return;

    const audio = audioRef.current;

    const muteVideo = () => {
      if (video) {
        video.muted = true;
        video.volume = 0;
      }
    };

    muteVideo();
    audio.volume = 1;

    const syncAudio = () => {
      if (video && audio && !video.paused) {
        const targetTime = Math.max(0, video.currentTime + offset);
        if (Math.abs(audio.currentTime - targetTime) > 0.15) {
          audio.currentTime = targetTime;
        }
      }
      muteVideo();
    };

    const handlePlay = async () => {
      setIsPlaying(true);
      muteVideo();
      if (audio) {
        try {
          audio.currentTime = Math.max(0, video.currentTime + offset);
          await audio.play();
        } catch (error) {
          console.error("Error playing audio:", error);
        }
      }
    };

    const handlePause = () => {
      setIsPlaying(false);
      if (audio) {
        audio.pause();
      }
    };

    const handleTimeUpdate = () => {
      syncAudio();
    };

    const handleSeeked = () => {
      muteVideo();
      if (audio) {
        audio.currentTime = Math.max(0, video.currentTime + offset);
      }
    };

    const handleVolumeChange = () => {
      muteVideo();
    };

    const handleLoadedMetadata = () => {
      muteVideo();
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("volumechange", handleVolumeChange);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    const syncInterval = setInterval(syncAudio, 50);
    const muteInterval = setInterval(muteVideo, 100);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("volumechange", handleVolumeChange);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      clearInterval(syncInterval);
      clearInterval(muteInterval);
      if (video) {
        video.muted = false;
        video.volume = 1;
      }
    };
  }, [player, offset, audioUrl]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleOffsetChange = (newOffset) => {
    setOffset(newOffset);
    localStorage.setItem(`audio_sync_${episodeId}`, newOffset.toString());
  };

  const adjustOffset = (delta) => {
    const newOffset = Math.max(-5, Math.min(5, offset + delta));
    handleOffsetChange(newOffset);
  };

  const resetOffset = () => {
    handleOffsetChange(0);
  };

  const formatOffset = (value) => {
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}s`;
  };

  if (!audioUrl) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-all duration-300 text-sm"
        title="تنظیم همگام‌سازی صدا"
      >
        <FaSync className="text-base" />
        <span className="font-medium">سینک صدا</span>
        {offset !== 0 && (
          <span className="text-xs text-yellow-400">{formatOffset(offset)}</span>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-gray-800 rounded-lg shadow-xl border border-gray-700 p-4 z-50">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-sm">تنظیم همگام‌سازی</h3>
              <button
                onClick={resetOffset}
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                بازنشانی
              </button>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => adjustOffset(-0.1)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  title="کاهش 0.1 ثانیه"
                >
                  <FaChevronLeft className="text-white text-xs" />
                </button>

                <div className="flex-1">
                  <input
                    type="range"
                    min="-5"
                    max="5"
                    step="0.1"
                    value={offset}
                    onChange={(e) => handleOffsetChange(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer audio-sync-slider"
                    style={{
                      background: `linear-gradient(to right, #b45309 0%, #b45309 ${((offset + 5) / 10) * 100}%, #374151 ${((offset + 5) / 10) * 100}%, #374151 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>-5s</span>
                    <span className="text-yellow-400 font-bold">{formatOffset(offset)}</span>
                    <span>+5s</span>
                  </div>
                </div>

                <button
                  onClick={() => adjustOffset(0.1)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  title="افزایش 0.1 ثانیه"
                >
                  <FaChevronRight className="text-white text-xs" />
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => adjustOffset(-0.5)}
                  className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-xs transition-colors"
                >
                  -0.5s
                </button>
                <button
                  onClick={() => adjustOffset(0.5)}
                  className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-xs transition-colors"
                >
                  +0.5s
                </button>
              </div>

              <div className="text-xs text-gray-400 text-center">
                {offset > 0
                  ? "صدا جلوتر از ویدیو است"
                  : offset < 0
                  ? "صدا عقب‌تر از ویدیو است"
                  : "صدا و ویدیو همگام هستند"}
              </div>
            </div>
          </div>
        </div>
      )}

      <audio
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        style={{ display: "none" }}
      />
    </div>
  );
};

export default AudioSync;

